import { Component, Prop, Vue } from 'vue-property-decorator';
import { AxiosError } from 'axios';
import { cloneDeep } from 'lodash';
import ErrorHandler from '@/support/ErrorHandler.ts';
import { BoxRequest, ReadInclude as BoxRequestReadInclude, StatusKey as BoxRequestStatusKey } from '@/models/BoxRequest.ts';
import { Options } from '@/components/mi-dialog/MiDialog.ts';
import ActionDeleteIcon from '@/components/action-icon/action-delete-icon/ActionDeleteIcon.vue';
import ActionEditIcon from '@/components/action-icon/action-edit-icon/ActionEditIcon.vue';
import ActionEditCancelIcon from '@/components/action-icon/action-edit-cancel-icon/ActionEditCancelIcon.vue';
import ActionSaveIcon from '@/components/action-icon/action-save-icon/ActionSaveIcon.vue';
import BlockInfo from '@/components/request/RequestDetail/block-info/BlockInfo.vue';
import BlockNotes from '@/components/request/RequestDetail/block-notes/BlockNotes.vue';
import BlockStatus from '@/components/request/RequestDetail/block-status/BlockStatus.vue';
import BlockLogs from '@/components/request/RequestDetail/block-logs/BlockLogs.vue';
import BlockType from '@/components/request/RequestDetail/block-type/BlockType.vue';
import LoaderCard from '@/components/loader-card/LoaderCard.vue';
import RequestButtonBack from "@/components/request/RequestButtonBack/RequestButtonBack.vue";
import BlockImages from "@/components/request/RequestDetail/block-images/BlockImages.vue";
import BlockOrders from "@/components/request/RequestDetail/block-orders/BlockOrders.vue";
import {User} from "@/models/User";

@Component<RequestDetail>({
  components: {
    'action-delete-icon': ActionDeleteIcon,
    'action-edit-icon': ActionEditIcon,
    'action-edit-cancel-icon': ActionEditCancelIcon,
    'action-save-icon': ActionSaveIcon,
    'block-info': BlockInfo,
    'block-notes': BlockNotes,
    'block-status': BlockStatus,
    'block-logs': BlockLogs,
    'block-type': BlockType,
    'block-images': BlockImages,
    'block-orders': BlockOrders,
    'loader-card': LoaderCard,
    'request-button-back': RequestButtonBack,

  }
})
export default class RequestDetail extends Vue {
  @Prop()
  private requestId!: string;

  private request: BoxRequest | null = null;

  private requestClone: BoxRequest | null = null;

  private isLoading = false;

  private isEditing = false;

  private stylists: User[] = [];

  private selectedStylist: User | null = null;

  private reset(): void {
    this.selectedStylist = null;
  }

  private mounted(): void {
    this.initialize();
  }

  private initialize(): void {
    this.fetchRequest();
    this.getStylists();
  }

  private async getStylists(search: string = ''): Promise<void> {
    const users: User[] = await new User()
      .filter({
        role: 'stylist',
        search: search,
        is_active: true,
      })
      .sort('name')
      .limit(999)
      .all()
      .catch((error: AxiosError): User[] => {
        ErrorHandler.network(error);
        return [];
      });

    this.stylists = users;
  }

  private handleEditCancelClick(): void {
    this.$store.dispatch('openDialog', this.editCancelConfirmDialog);
  }

  private handleSaveClick(): void {
    this.$store.dispatch('openDialog', this.saveConfirmDialog);
  }

  private get showOrderButton(): boolean {
    if (! this.request || ! this.request.status) {
      return false;
    }

    return ! ['pending','planned', 'on_hold_photos', 'on_hold_credit_check', 'on_hold_budget'].includes(this.request.status);
  }

  private editCancel(): void {
    this.request = cloneDeep(this.requestClone);
    this.isEditing = false;
  }

  private save(): void {
    this.updateRequest();
  }

  private async fetchRequest(): Promise<void> {
    if (! this.id) { return; }
    this.isLoading = true;
    const request: BoxRequest | null = await new BoxRequest()
      .include(this.includes)
      .find(this.id)
      .catch((error: AxiosError): null => {
        ErrorHandler.network(error);
        return null;
      });
    this.request = request;
    if (request && request.stylist) {
      // @ts-ignore
      this.selectedStylist = request.stylist;
    }
    this.requestClone = cloneDeep(request);
    this.isLoading = false;
  }

  private async updateRequest(): Promise<void> {

    if (! this.id || ! this.request) { return; }
    this.isLoading = true;
    const request: BoxRequest | null = await this.request
      .include(this.includes)
      .update(this.request)
      .catch((error: AxiosError): null => {
        ErrorHandler.network(error);
        return null;
      });
    if (! request) {
      this.isLoading = false;
      return;
    }
    this.request = request;
    this.requestClone = cloneDeep(request);
    this.isLoading = false;
    this.isEditing = false;
  }

  private async updateRequestStatus(status: BoxRequestStatusKey): Promise<void> {
    if (! this.id || ! this.request) { return; }
    this.isLoading = true;
    const payload: { status: BoxRequestStatusKey } = { status };
    const request: BoxRequest | null = await this.request
      .include(this.includes)
      .update(payload)
      .catch((error: AxiosError): null => {
        ErrorHandler.network(error);
        return null;
      });
    if (! request) {
      this.isLoading = false;
      return;
    }
    this.request.status = status;
    if (this.requestClone) {
      this.requestClone.status = status;
    }
    this.isLoading = false;
  }

  private get id(): string {
    if (this.requestId) { return this.requestId; }
    if (this.$route.params.id) { return this.$route.params.id; }
    return '';
  }


  private get includes(): BoxRequestReadInclude[] {
    return ['info', 'images', 'user', 'orders', 'stylist', 'logs', 'referral_code'];
  }

  private get editCancelConfirmDialog(): Options {
    return {
      title: this.$t('dialogOptions.confirmation').toString(),
      text: 'Wil je de gemaakte wijzigingen ongedaan maken?',
      type: 'warning',
      buttons: {
        confirm: {
          text: 'Ja, wijzigingen ongedaan maken',
          action: (): void => {
            this.editCancel();
          }
        },
        cancel: {
          text: this.$t('dialogOptions.button.cancel').toString(),
        }
      }
    };
  }

  private get saveConfirmDialog(): Options {
    return {
      title: this.$t('dialogOptions.confirmation').toString(),
      text: 'Wil je de gemaakte wijzigingen opslaan?',
      type: 'warning',
      buttons: {
        confirm: {
          text: 'Ja, wijzigingen opslaan',
          action: (): void => {
            this.save();
          }
        },
        cancel: {
          text: this.$t('dialogOptions.button.cancel').toString(),
        }
      }
    };
  }

  get createdAt() : string {
    if (! this.request || ! this.request.created_at) {
      return '';
    }

    const dateTime = this.request.created_at;
    const date = dateTime.split(' ')[0];
    const dateNL = date.split('-').reverse().join('-');
    if (dateNL == '00-00-0000') {
      return '';
    }
    return dateNL;
  }

  get assignedAt() : string {
    if (! this.request || ! this.request.assigned_at) {
      return '';
    }

    const dateTime = this.request.assigned_at;
    const date = dateTime.split(' ')[0];
    const dateNL = date.split('-').reverse().join('-');
    if (dateNL == '00-00-0000') {
      return '';
    }
    return dateNL;
  }

  private get isPlanner() {
    if (! this.$store || !this.$store.state || ! this.$store.state.Auth) {
      return false;
    }

    // @ts-ignore
    return this.$store.state.Auth.isPlanner()
  }

  private async updateStylist(): Promise<void> {
    if (! this.request) {
      return;
    }

    const request: BoxRequest = await this.request
      .update({
        stylist: this.selectedStylist && this.selectedStylist.id ? this.selectedStylist.id : '',
      })
      .catch((error: AxiosError): null => {
        ErrorHandler.network(error);
        return null;
      });
    this.$emit('refresh');
  }
}
